import { computed, reactive, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil, upperFirst } from 'lodash';

import { toPresenterPlaceholder } from '@satellite/composables/presenterPlaceholder';
import { YardTaskStatus } from '@nova/core';
import { isMoveTask } from '@satellite/helpers/yardTaskUtils';
import { nestedAssetPresenterDataProps } from '@satellite/composables/presenterNestedAssetProps';
import { nestedSpotPresenterDataProps } from '@satellite/composables/presenterNestedSpotProps';
import { useYardTaskDisplay } from '@satellite/composables/useYardTaskDisplay';
import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe, MoveTaskViewData, TaskViewData } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { PresenterDisplayOverrideProps } from '@satellite/composables/presenterDisplayOptions';
import type { YardAreaDetails } from '@satellite/composables/yardAreaPresenterShared';
import type { YardAssetDetails } from '@satellite/composables/yardAssetPresenterShared';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenter';
import type { UseYardTaskDisplayOptions } from '@satellite/composables/useYardTaskDisplay';
import type { YardTaskDetails } from './yardTaskPresenterShared';

export type YardMoveTaskDisplayField = 'asset' | 'originSpot' | 'destinationSpot';

export interface UseYardMoveTaskDisplayOptions extends UseYardTaskDisplayOptions {
  assetProps?: PresenterDisplayOverrideProps;
  originSpotProps?: PresenterDisplayOverrideProps;
  destinationSpotProps?: PresenterDisplayOverrideProps;
}

function moveSpotPlaceholder(
  spot: Maybe<{ id: string; code?: string; type?: string; spotAreaId?: string }>
): PresenterPlaceholderData<YardSpotDetails> | undefined {
  if (!spot?.id) {
    return undefined;
  }
  return toPresenterPlaceholder<YardSpotDetails>({
    id: spot.id,
    code: spot.code ?? '',
    type: spot.type ?? '',
    spotAreaId: spot.spotAreaId ?? ''
  });
}

function moveAreaPlaceholder(
  area: Maybe<{ id: string; name?: string }>
): PresenterPlaceholderData<YardAreaDetails> | undefined {
  if (!area?.id) {
    return undefined;
  }
  return toPresenterPlaceholder<YardAreaDetails>({
    id: area.id,
    name: area.name
  });
}

function moveAssetPlaceholder(
  asset: Maybe<{ id: string; code?: string; type?: string }>
): PresenterPlaceholderData<YardAssetDetails> | undefined {
  if (!asset?.id) {
    return undefined;
  }
  return toPresenterPlaceholder<YardAssetDetails>({
    id: asset.id,
    code: asset.code,
    type: asset.type
  });
}

/** The task-list endpoint nests the move fields under `child`; `MoveTaskViewData` keeps them flat. */
type NestedMoveTask = MoveTaskViewData & {
  child?: Maybe<Partial<MoveTaskViewData> & { destinationSpotId?: Maybe<string> }>;
};

function moveFields(task: TaskViewData): NestedMoveTask {
  return task as NestedMoveTask;
}

export function getMoveTaskAssetContainer(task: TaskViewData): Maybe<YardAssetDetails> {
  const move = moveFields(task);

  return (move.assetContainer ?? move.child?.assetContainer) as Maybe<YardAssetDetails>;
}

export function getMoveTaskOriginSpot(task: TaskViewData): Maybe<YardSpotDetails> {
  const move = moveFields(task);

  return (move.originSpot ?? move.child?.originSpot) as Maybe<YardSpotDetails>;
}

export function getMoveTaskDestinationSpot(task: TaskViewData): Maybe<YardSpotDetails> {
  const move = moveFields(task);

  return (move.destinationSpot ?? move.child?.destinationSpot) as Maybe<YardSpotDetails>;
}

export function getMoveTaskDestinationSpotId(task: Maybe<TaskViewData>): Maybe<string> {
  if (!task) {
    return null;
  }

  return getMoveTaskDestinationSpot(task)?.id ?? moveFields(task).child?.destinationSpotId ?? null;
}

/**
 * Started moves aiming *at* this spot. A task leaving it is not something arriving, and one still in
 * To Do has no asset between spots yet — the same rule the grid ghost follows.
 */
export function getInboundMoveTasks<T extends TaskViewData>(
  tasks: Maybe<T[]>,
  spotId: Maybe<string>
): T[] {
  if (!spotId) {
    return [];
  }

  return (tasks ?? []).filter(
    task =>
      task.status === YardTaskStatus.IN_PROGRESS && getMoveTaskDestinationSpotId(task) === spotId
  );
}

export function getMoveTaskOriginSpotArea(task: TaskViewData): Maybe<YardAreaDetails> {
  const move = moveFields(task);

  return (move.originSpotArea ?? move.child?.originSpotArea) as Maybe<YardAreaDetails>;
}

function getMoveTaskDestinationSpotArea(task: TaskViewData) {
  const move = moveFields(task);

  return move.destinationSpotArea ?? move.child?.destinationSpotArea;
}

export function useYardMoveTaskDisplay(
  task: Ref<YardTaskDetails | null> | ComputedRef<YardTaskDetails | null>,
  options: UseYardMoveTaskDisplayOptions = {}
) {
  const { assetProps, originSpotProps, destinationSpotProps, ...taskDisplayOptions } = options;

  const { t } = useI18n();

  const taskDisplay = useYardTaskDisplay(task, taskDisplayOptions);
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);

  const moveTypeLabel = computed(() => {
    const currentTask = task.value;

    if (!currentTask?.type) {
      return '';
    }

    if (isMoveTask(currentTask)) {
      return t('global.tasks.presenter.types.move');
    }

    return upperFirst(currentTask.type);
  });

  const isMoveTaskValue = computed(() => !isNil(task.value) && isMoveTask(task.value));

  const assetContainerId = computed(() => {
    if (!task.value) {
      return null;
    }
    return getMoveTaskAssetContainer(task.value)?.id ?? null;
  });

  const originSpotId = computed(() => {
    if (!task.value) {
      return null;
    }
    return getMoveTaskOriginSpot(task.value)?.id ?? null;
  });

  const destinationSpotId = computed(() => getMoveTaskDestinationSpotId(task.value) ?? null);

  const assetPresenterProps = computed(() => {
    const assetOverrideProps = toValue(assetProps) ?? {};

    if (!task.value) {
      return assetOverrideProps;
    }

    return {
      ...nestedAssetPresenterDataProps(displayMode.value, {
        asset: moveAssetPlaceholder(getMoveTaskAssetContainer(task.value)) ?? null
      }),
      ...assetOverrideProps
    };
  });

  const originSpotPresenterProps = computed(() => {
    const originSpotOverrideProps = toValue(originSpotProps) ?? {};

    if (!task.value) {
      return originSpotOverrideProps;
    }

    return {
      ...nestedSpotPresenterDataProps(displayMode.value, {
        spot: moveSpotPlaceholder(getMoveTaskOriginSpot(task.value)) ?? null,
        spotArea: moveAreaPlaceholder(getMoveTaskOriginSpotArea(task.value)) ?? null
      }),
      ...originSpotOverrideProps
    };
  });

  const destinationSpotPresenterProps = computed(() => {
    const destinationSpotOverrideProps = toValue(destinationSpotProps) ?? {};

    if (!task.value) {
      return destinationSpotOverrideProps;
    }

    return {
      ...nestedSpotPresenterDataProps(displayMode.value, {
        spot: moveSpotPlaceholder(getMoveTaskDestinationSpot(task.value)) ?? null,
        spotArea: moveAreaPlaceholder(getMoveTaskDestinationSpotArea(task.value)) ?? null
      }),
      ...destinationSpotOverrideProps
    };
  });

  const anyAssetLabel = computed(() => t('global.tasks.presenter.anyAsset'));
  const fromAnySpotLabel = computed(() => t('global.tasks.presenter.fromAnySpot'));
  const toAnySpotLabel = computed(() => t('global.tasks.presenter.toAnySpot'));

  return reactive({
    taskDisplay,
    mode: displayMode,
    moveTypeLabel,
    isMoveTask: isMoveTaskValue,
    assetContainerId,
    originSpotId,
    destinationSpotId,
    assetPresenterProps,
    originSpotPresenterProps,
    destinationSpotPresenterProps,
    anyAssetLabel,
    fromAnySpotLabel,
    toAnySpotLabel
  });
}

export type YardMoveTaskDisplayData = ReturnType<typeof useYardMoveTaskDisplay>;
